import moment from "moment";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import {
    type AccountingIntegrationType,
    PaymentApplicationStatus,
    type PaymentBrands,
    PaymentMethodType,
} from "types/enum";
import { type PaymentType } from "types/enum";

import { checkForDateInsideMinMax } from "utilities/date/date";

import { EditorContent } from "commonComponents/btWrappers/editor/editor.types";
import {
    type CheckoutErrorTypes,
    PaymentMethod,
} from "commonComponents/financial/Checkout/Checkout.api.types";
import {
    type IOnlinePaymentLimitsFormValues,
    type OnlinePaymentLimitsApiData,
    OnlinePaymentLimitsEntity,
} from "commonComponents/financial/OnlinePaymentLimits/OnlinePaymentLimits.api.types";
import { OutboundPaymentSetupData } from "commonComponents/financial/OutboundPaymentSetupWizard/OutboundPaymentSetupWizard.types";
import { type IOutboundPaymentSettingsFormValues } from "commonComponents/financial/OutboundPaymentSetupWizard/Steps/OutboundPaymentCompanyInfo.types";
import { type ICanVoidPayment } from "commonComponents/financial/PaymentVoidConfirm/PaymentVoidConfirm";

import { type CustomFinancialBannerNotificationData } from "entity/onlinePayment/AdminOnlinePaymentReports/OnlinePaymentAccountStatus/OnlinePaymentAccount/OnlinePaymentAccount.api.types";
import { CurrentMonthlyLimitInfoResponse } from "entity/onlinePayment/common/Limits/Limits.api.types";
import { type MicrodepositVerificationResponse } from "entity/onlinePayment/OnlinePayment.api.types";
import type { default as OnlinePaymentSettingsJson } from "entity/onlinePayment/OnlinePaymentSettings/OnlinePaymentSettings.api.json";

export type OnlinePaymentSettingsFormActions =
    | undefined
    | "save"
    | "cancel"
    | "manageAccounts"
    | "addBankAccount"
    | "addOutboundPaymentSubscription";

export interface IOnlinePaymentSettingsFormValues extends IOutboundPaymentSettingsFormValues {
    paymentOptions: IOnlinePaymentLimitsFormValues | null;
    defaultProposalPaymentType: number;
    defaultPaymentMethod: string;
    preferredPaymentMethodCredit: number | null;
    preferredPaymentMethodCheck: number | null;
    customerPaysCreditCardFeesForLeadProposals: boolean;
    enableChecksForSubcontractorPayments: boolean;
    startingCheckNumber: number;
}

export interface IDeletePaymentMethodOptions {
    deleteFromAccounting?: boolean;
    replacementId?: number;
    pendingPaymentAction?: PendingPaymentAction;
}

export const PaymentTypeNames = new Map<string, PaymentMethodType>([
    ["ACH", PaymentMethodType.Check],
    ["Bank Account", PaymentMethodType.Check],
    ["Credit Card", PaymentMethodType.CreditCard],
    ["Credit Card or ACH", PaymentMethodType.All],
    ["Credit Card or Bank Account", PaymentMethodType.All],
]);

export const NewMerchantPaymentMethodId = -1;

export class OnlinePaymentSettingsEntity {
    constructor(data: typeof OnlinePaymentSettingsJson) {
        this.americanBuilder = data.americanBuilder;
        this.enableBuilderAcceptPayments = data.enableBuilderAcceptPayments;
        this.builderAllowedSetup = data.builderAllowedSetup;
        this.enableOnlinePaymentsOnPurchaseOrders = data.enableOnlinePaymentsOnPurchaseOrders;
        this.paymentMethodTypes = data.paymentMethodTypes.map(
            (t: (typeof OnlinePaymentSettingsJson.paymentMethodTypes)[0]) => t.text,
        );
        this.defaultPurchaseOrderPaymentType = data.defaultPurchaseOrderPaymentType;
        this.builderIdCheck = data.builderIdCheck;
        this.referer = data.referer;
        this.currentLoginId = data.currentLoginId;
        this.outboundPaymentMethods = data.outboundPaymentMethods
            ? data.outboundPaymentMethods.map((payment) => new MerchantPaymentMethodSetup(payment))
            : [];
        this.payoutAccount = data.payoutAccount
            ? new MerchantPaymentMethodSetup(data.payoutAccount)
            : null;
        this.proposalPaymentTypes = data.proposalPaymentTypes.map(
            (t: (typeof OnlinePaymentSettingsJson.proposalPaymentTypes)[0]) => t.name,
        );
        this.selectedPaymentType = data.selectedPaymentType;
        this.isMerchantSetup = data.isMerchantSetup;
        this.accountingIsConnected = data.accountingIsConnected;
        this.accountingImageUrl = data.accountingImageUrl;
        this.integrationType = data.integrationType;
        this.paymentOptionsSetup = data.paymentOptionsSetup
            ? new OnlinePaymentLimitsEntity(
                  data.paymentOptionsSetup as unknown as OnlinePaymentLimitsApiData,
              )
            : null;
        this.lastDepositDate = checkForDateInsideMinMax(data.lastDepositDate);
        this.learnMoreUrl = data.learnMoreUrl;
        this.feeDetails = data.feeDetails
            ? data.feeDetails.map(
                  (fee: (typeof OnlinePaymentSettingsJson.feeDetails)[0]) =>
                      new FeeDisclosureLineItem(fee),
              )
            : [];
        this.voidPaymentAlertInfo = data.voidPaymentAlertInfo;
        this.additionalVoidAlert = data.additionalVoidAlert;
        this.showOutboundPaymentSetup = data.showOutboundPaymentSetup;
        this.isOutboundConnected = data.isOutboundConnected;
        this.outboundDataInfo = data.outboundDataInfo
            ? new OutboundPaymentSetupData(data.outboundDataInfo)
            : null;
        this.outboundPaymentSubscriptionItemInfo = new OutboundPaymentSubscriptionItemInfo(
            data.outboundPaymentSubscriptionItemInfo,
        );
        this.isInboundPPSVerified = data.isInboundPPSVerified;
        this.inboundOnboardingRejectionDate = data.inboundOnboardingRejectionDate
            ? moment(data.inboundOnboardingRejectionDate)
            : null;
        this.clientPaymentsApplicationStatus =
            data.clientPaymentsApplicationStatus ?? PaymentApplicationStatus.None;
        this.billPayApplicationStatus =
            data.billPayApplicationStatus ?? PaymentApplicationStatus.None;
        this.hasAcceptedTermsAndConditions = data.hasAcceptedTermsAndConditions;
        this.electronicConsentDocumentUrl = data.electronicConsentDocumentUrl;
        this.hasRequestedDocuments = data.hasRequestedDocuments;
        this.paymentLimits = data.paymentLimits
            ? new CurrentMonthlyLimitInfoResponse(data.paymentLimits)
            : null;
        this.displayDemoBanner = data.displayDemoBanner;
        this.paymentSpecialistContactInfo = data.paymentSpecialistContactInfo
            ? new PaymentSpecialistContactInfo(data.paymentSpecialistContactInfo)
            : null;
        this.primaryContactEmail = data.primaryContactEmail;
        this.passCreditCardFeesToCustomersForLeadProposals =
            data.passCreditCardFeesToCustomersForLeadProposals;
        this.customFinancialBannerNotificationData = data.customFinancialBannerNotificationData;
        this.customFinancialBannerNotificationMessageData = data
            .customFinancialBannerNotificationData?.messageHtml
            ? new EditorContent(data.customFinancialBannerNotificationData.messageHtml)
            : null;
        this.hasLegacyAch = data.hasLegacyAch;
        this.hasOutboundAch = data.hasOutboundAch;
        this.orgOwners =
            data.orgOwners?.map((x: (typeof OnlinePaymentSettingsJson.orgOwners)[0]) => {
                return new BTSelectItem(x);
            }) ?? [];
        this.trustedUsersOptions =
            data.trustedUsersOptions?.map(
                (x: (typeof OnlinePaymentSettingsJson.trustedUsersOptions)[0]) =>
                    new BTSelectItem(x),
            ) ?? [];
        this.isCurrentUserOrgOwner = data.isCurrentUserOrgOwner;
        this.enableChecksForSubcontractorPayments =
            data.enableChecksForSubcontractorPayments ?? true;
        this.hasOutboundChecks = data.hasOutboundChecks ?? true;
        this.printedChecksEnabled = data.printedChecksEnabled ?? true;
        this.showCheckSetup = data.showCheckSetup ?? false;
        this.checkCapabilityDisabledReason = data.checkCapabilityDisabledReason ?? null;
        this.paymentMethodIdsWithCompletedAch = data.paymentMethodIdsWithCompletedAch ?? [];
        this.hasInvalidSnapshot = data.hasInvalidSnapshot;
        this.canUpdateBankAccountInfo = data.canUpdateBankAccountInfo ?? false;
        this.canLinkBankAccounts = data.canLinkBankAccounts ?? false;
    }

    americanBuilder: boolean;
    enableBuilderAcceptPayments: boolean;
    builderAllowedSetup: boolean;
    enableOnlinePaymentsOnPurchaseOrders: boolean;
    paymentMethodTypes: string[];
    defaultPurchaseOrderPaymentType: number;
    builderIdCheck: string;
    referer: string;
    currentLoginId: number;
    payoutAccount: MerchantPaymentMethodSetup | null;
    outboundPaymentMethods: MerchantPaymentMethodSetup[];
    proposalPaymentTypes: string[];
    selectedPaymentType: number;
    isMerchantSetup: boolean;
    accountingIsConnected: boolean;
    accountingImageUrl: string;
    integrationType: AccountingIntegrationType;
    paymentOptionsSetup: OnlinePaymentLimitsEntity | null;
    lastDepositDate?: moment.Moment;
    learnMoreUrl: string;
    feeDetails: FeeDisclosureLineItem[];
    voidPaymentAlertInfo: ICanVoidPayment;
    additionalVoidAlert?: string;
    showOutboundPaymentSetup: boolean;
    isOutboundConnected: boolean;
    outboundDataInfo: OutboundPaymentSetupData | null;
    outboundPaymentSubscriptionItemInfo: OutboundPaymentSubscriptionItemInfo;
    isInboundPPSVerified: boolean;
    inboundOnboardingRejectionDate: moment.Moment | null;
    clientPaymentsApplicationStatus: PaymentApplicationStatus;
    billPayApplicationStatus: PaymentApplicationStatus;
    hasAcceptedTermsAndConditions: boolean;
    electronicConsentDocumentUrl?: string;
    hasRequestedDocuments: boolean;
    paymentLimits: CurrentMonthlyLimitInfoResponse | null;
    displayDemoBanner: boolean;
    paymentSpecialistContactInfo: PaymentSpecialistContactInfo | null;
    primaryContactEmail: string;
    passCreditCardFeesToCustomersForLeadProposals: boolean;
    customFinancialBannerNotificationData: CustomFinancialBannerNotificationData;
    customFinancialBannerNotificationMessageData: EditorContent | null;
    hasLegacyAch: boolean;
    hasOutboundAch: boolean;
    hasOutboundChecks: boolean;
    printedChecksEnabled: boolean;
    showCheckSetup: boolean;
    checkCapabilityDisabledReason: number | null;
    orgOwners: BTSelectItem<string>[];
    trustedUsersOptions: BTSelectItem<string>[];
    isCurrentUserOrgOwner: boolean;
    enableChecksForSubcontractorPayments: boolean;
    paymentMethodIdsWithCompletedAch: number[];
    hasInvalidSnapshot: boolean;
    canUpdateBankAccountInfo: boolean;
    canLinkBankAccounts: boolean;
}

export class OutboundPaymentSubscriptionItemInfo {
    constructor(data: any) {
        this.outboundPaymentsState = data.outboundPaymentsState;
        this.chargePerAch = data.chargePerAch;
        this.chargePerCheck = data.chargePerCheck;
        this.isAnnualBilling = data.isAnnualBilling;
    }
    outboundPaymentsState: OutboundPaymentsState;
    chargePerAch: number | null;
    chargePerCheck: number | null;
    isAnnualBilling: boolean | null;
}

export enum OutboundPaymentsState {
    Enabled = 0,
    PlanUpgradeRequired = 1,
    SubscriptionRequired = 2,
}

export class MerchantPaymentMethodSetup {
    constructor(data: any) {
        this.id = data.id;
        this.accountingPaymentMethodId = data.accountingPaymentMethodId ?? undefined;
        this.canSendToAccounting = data.canSendToAccounting;
        this.name = data.name;
        this.paymentType = data.paymentType;
        this.isDefaultPayment = data.isDefaultPayment;
        this.isInboundPayoutAccount = data.isInboundPayoutAccount;
        this.paymentsInProgress = data.paymentsInProgress;
        this.paymentTypeEnumDescription = data.paymentTypeEnumDescription;
        this.needsMicrodepositVerification = false; // Clear-ToDo - Update this to be based on the complex response object added in ado-27714
        this.onlyAllowAchDeposits = data.onlyAllowAchDeposits;
        this.paymentMethodInfo = data.paymentMethodInfo
            ? new PaymentMethod(data.paymentMethodInfo)
            : null;
        this.pendingPayments = data.pendingPayments
            ? data.pendingPayments.map((x: any) => new PendingPaymentsResponse(x))
            : null;
    }

    id: number;
    accountingPaymentMethodId?: number;
    canSendToAccounting: boolean;
    name: string;
    paymentType: PaymentType;
    isDefaultPayment: boolean;
    isInboundPayoutAccount: boolean;
    paymentsInProgress: number;
    paymentTypeEnumDescription: string;
    needsMicrodepositVerification: boolean;
    onlyAllowAchDeposits?: boolean;
    paymentMethodInfo: PaymentMethod | null;
    pendingPayments: PendingPaymentsResponse[] | null;
}

export class PendingPaymentsResponse {
    constructor(data: any) {
        this.merchantPaymentId = data.merchantPaymentId;
        this.amount = data.amount;
        this.checkNumber = data.checkNumber;
    }

    merchantPaymentId: number;
    amount: number;
    checkNumber: string;
}

export enum PendingPaymentAction {
    Void = 1,
    ChangePaymentMethod = 2,
}

export class OnlinePaymentSettingsUpdateResponse {}

export class FeeDisclosureLineItem {
    constructor(data: any) {
        this.title = data.title;
        this.value = data.value;
        this.tooltip = data.tooltip ?? undefined;
        this.termsAndConditionsDisplay = data.termsAndConditionsDisplay;
    }

    title: string;
    value: string;
    tooltip?: string;
    termsAndConditionsDisplay: string;
}

export class OnlinePaymentSettingsActionResponse {
    constructor(data: any) {
        this.alertMessage = data.alertMessage;
        this.setupResponse = data.setupResponse
            ? new OnlinePaymentSettingsEntity(data.setupResponse)
            : null;
    }

    alertMessage: string | null;
    setupResponse: OnlinePaymentSettingsEntity | null;
}

export class MerchantPaymentDeleteResponse {
    constructor(data: any) {
        this.totalPendingPayments = data.totalPendingPayments;
        this.totalPaymentsVoided = data.totalPaymentsVoided;
        this.paymentFailedToUpdate = data.paymentFailedToUpdate;
    }

    totalPendingPayments: number;
    totalPaymentsVoided: number;
    paymentFailedToUpdate: boolean;
}

export class PaymentMethodDto {
    constructor(data: any) {
        this.merchantPaymentMethodId = data.merchantPaymentMethodId;
        this.processorPaymentMethodId = data.processorPaymentMethodId;
        this.payerName = data.payerName;
        this.paymentType = data.paymentType;
        this.lastFour = data.lastFour;
        this.paymentBrand = data.paymentBrand;
        this.payerEmail = data.payerEmail;
        this.payerPhoneNumber = data.payerPhoneNumber;
        this.accountingId = data.accountingId;
        this.needsMicrodepositVerification = data.needsMicrodepositVerification;
        this.isDefault = data.isDefault;
        this.connectedToAccounting = data.connectedToAccounting;
        this.accountingSystemName = data.accountingSystemName;
        this.routingNumber = data.routingNumber;
        this.checkoutError = data.checkoutError;
        this.feePercent = data.feePercent;
        this.flatFee = data.flatFee;
        this.isFromMobileWallet = data.isFromMobileWallet;
    }

    merchantPaymentMethodId: number | null;
    processorPaymentMethodId: string | null;
    payerName: string;
    paymentType: PaymentMethodType;
    lastFour: string;
    paymentBrand: PaymentBrands | null;
    payerEmail?: string;
    payerPhoneNumber?: string;
    needsMicrodepositVerification?: boolean;
    accountingId?: number;
    isDefault: boolean;
    connectedToAccounting: boolean;
    accountingSystemName: string;
    routingNumber?: string;
    checkoutError: CheckoutErrorTypes | null;
    feePercent: number;
    flatFee: number;
    isFromMobileWallet: boolean;
}

export class SetupPaymentMethodsResponse {
    constructor(data: any) {
        this.paymentMethods = data.paymentMethods
            ? data.paymentMethods.map((payment: any) => new MerchantPaymentMethodSetup(payment))
            : [];
        this.payoutAccount = data.payoutAccount
            ? new MerchantPaymentMethodSetup(data.payoutAccount)
            : null;
    }
    paymentMethods: MerchantPaymentMethodSetup[];
    payoutAccount: MerchantPaymentMethodSetup | null;
}

export class PaymentServiceAccountConfigResponse {
    constructor(data: any) {
        this.iframeToken = data.iframeToken;
        this.baseUrl = data.baseUrl;
        this.allowedCardTypes = data.allowedCardTypes ? data.allowedCardTypes : [];
    }

    iframeToken: string;
    baseUrl: string;
    allowedCardTypes: PaymentBrands[];
}

export class ExternalPaymentMethodVerificationResponse {
    constructor(data: any) {
        this.paymentMethod = data.paymentMethod;
        this.verificationResponse = data.verificationResponse;
        this.bankAccountConfig = data.bankAccountConfig;
        this.associatedPaymentInfo = data.associatedPaymentInfo ?? null;
    }

    paymentMethod: PaymentMethodDto;
    verificationResponse: MicrodepositVerificationResponse;
    bankAccountConfig: PaymentServiceAccountConfigResponse;
    associatedPaymentInfo: MicrodepositAssociatedPaymentInfo | null;
}

export interface IMicrodepositAssociatedPaymentInfoApiType {
    amount: number;
    recipientName: string;
    statementDescription: string;
    numberOfPayments: number;
}

export class MicrodepositAssociatedPaymentInfo {
    constructor(data: IMicrodepositAssociatedPaymentInfoApiType) {
        this.amount = data.amount;
        this.recipientName = data.recipientName;
        this.statementDescription = data.statementDescription;
        this.numberOfPayments = data.numberOfPayments ?? 1;
    }
    amount: number;
    recipientName: string;
    statementDescription: string;
    numberOfPayments: number;
}

export interface IPaymentSpecialistContactInfo {
    name: string;
    email: string;
    phone: string;
}

export class PaymentSpecialistContactInfo {
    constructor(data: IPaymentSpecialistContactInfo) {
        this.name = data.name;
        this.email = data.email;
        this.phone = data.phone;
    }
    name: string;
    email: string;
    phone: string;
}

export const MaxTransactionLimit = 350000;
